/* eslint-disable radix */
import request, { BASE_URL } from '../utils/request.utils';

export async function fetchAllThing() {
  return request('/thing/all', 'GET');
}

export async function fetchOneThing(thingId) {
  return request(`/thing/one?id=${thingId}`);
}

export async function fetchAllParques() {
  return request(`/api/v1/park/dashboard`);
}

// Param `date` (ISO 8601) e opcional. Quando informado, o backend
// retorna [date - intervalDays, date] — habilita comparar com janelas
// passadas (ghost line "vs ontem" no LineChart).
const buildHistoricUrl = (type, variableIhmName, thingIhmName, date) => {
  let url = `api/v1/variable/${type}-historic?variableIhmName=${variableIhmName}&thingIhmName=${thingIhmName}`;
  if (date) url += `&date=${encodeURIComponent(date)}`;
  return url;
};

export async function fetchHistoricType(
  variableIhmName,
  thingIhmName,
  type,
  date
) {
  return request(buildHistoricUrl(type, variableIhmName, thingIhmName, date));
}

export async function fetchHistoricDay(variableIhmName, thingIhmName, date) {
  return request(
    buildHistoricUrl('daily', variableIhmName, thingIhmName, date)
  );
}

export async function fetchHistoricWeek(variableIhmName, thingIhmName, date) {
  return request(
    buildHistoricUrl('weekly', variableIhmName, thingIhmName, date)
  );
}

export async function fetchHistoricMonth(variableIhmName, thingIhmName, date) {
  return request(
    buildHistoricUrl('monthly', variableIhmName, thingIhmName, date)
  );
}

export async function fetchUser() {
  return request(`api/v1/user/me`);
}

// Atualiza preferencias do usuario autenticado (merge parcial no blob
// JSON `preferences`). Ex.: updateUserPreferences({ theme: 'dark' }).
// Generico — qualquer chave de preferencia de UI (tema e futuras)
// passa por aqui. O backend faz merge raso, preservando outras chaves.
export async function updateUserPreferences(partial) {
  return request(`api/v1/user/me/preferences`, 'PATCH', partial);
}

export async function fetchZones(isCompany) {
  return request(`api/v1/zone/list?isCompany=${isCompany}`);
}

export async function getSearchParquesClientes(search) {
  return request(`api/v1/park/searchZoneOrCustomer?search=${search}`);
}

export async function fetchAllParks() {
  return request(`api/v1/park/list`);
}

export async function fetchAllParksCustomer(customerId) {
  return request(`api/v1/park/list?customerId=${customerId}&zoneId=[]`);
}

export async function fetchAllCustomersCompany(companyId) {
  return request(`api/v1/company/${companyId}/customers`);
}

export async function fetchCustomerData(customerId) {
  return request(`api/v1/park/customer/${customerId}/summary/total`);
}

export async function fetchParksByZone(zoneId, customerId) {
  return request(
    `api/v1/park/list?zoneId=${JSON.stringify(zoneId)}${
      customerId ? `&customerId=${customerId}` : ''
    }`
  );
}

export async function fetchCustomersByZone(zoneId, companyId) {
  return request(
    `api/v1/zone/customers?zoneId=${zoneId}&companyId=${companyId}`
  );
}

export async function fetchAllCustomers(companyId) {
  return request(`api/v1/zone/customers?&companyId=${companyId}`);
}

export async function addVariableToUser(newVar, thingId) {
  return request(`/api/v1/user/add-variable-to-user`, 'POST', {
    variables: [parseInt(newVar)],
    thingId: parseInt(thingId),
  });
}

export async function removeVariableToUser(newVar, thingId) {
  return request(`/api/v1/user/remove-user-variable`, 'DELETE', {
    variables: [parseInt(newVar)],
    thingId: parseInt(thingId),
  });
}

export async function removeUserVariables(body) {
  return request(`/api/v1/user/remove-user-variable`, 'DELETE', body);
}

export async function getDataDashboard(id, type, signal) {
  return request(
    `api/v1/${type}/${id}/summary/total`,
    'GET',
    null,
    null,
    undefined,
    signal
  );
}

export async function getSocketDataByType(id, type) {
  return request(`api/v1/${type}/${id}/summary/total`);
}

export async function getAllVariablesAmount(thingId) {
  return request(`api/v1/variable/amount?thingId=${thingId}`);
}

// ============================================================================
// Variable CRUD admin (tela /variables + /variables/new)
// Endpoint backend `/variable/filtered` aceita filtros cruzados por
// thing/customer/appliance/device/type + paginacao. POST/PATCH/DELETE
// padrao REST.
// ============================================================================

// Listagem paginada com filtros cruzados. Response shape:
//   { data: [{ id, name, description, type, ranges[], status }], total, page, limit, totalPages }
// Todos os params sao opcionais. `name` filtra por ihmname (LIKE),
// `description` por nome legivel (LIKE). `type` = label do tipo
// (case-insensitive).
export async function getVariablesFiltered({
  name,
  description,
  type,
  thingId,
  customerId,
  device,
  applianceId,
  page = 1,
  limit = 10,
  signal,
} = {}) {
  const params = new URLSearchParams();
  if (name) params.append('name', name);
  if (description) params.append('description', description);
  if (type) params.append('type', type);
  if (thingId) params.append('thingId', thingId);
  if (customerId) params.append('customerId', customerId);
  if (device) params.append('device', device);
  if (applianceId) params.append('applianceId', applianceId);
  params.append('page', page);
  params.append('limit', limit);
  return request(
    `/api/v1/variable/filtered?${params.toString()}`,
    'GET',
    null,
    null,
    undefined,
    signal
  );
}

// Lista TODAS as variaveis (sem paginacao). Usado pra autocomplete /
// listagem completa quando precisar exibir todo o catalogo.
export async function getAllVariablesAdmin() {
  return request(`/api/v1/variable/total`);
}

// Lista global de tipos de variavel — popula dropdown de tipo no
// cadastro. Response: { data: [{ id, label, group, isSetpoint }] }.
// Inclui tanto variabletype quanto setpoint groups (isSetpoint=true).
export async function getVariableTypes() {
  return request(`/api/v1/variable/types`);
}

// Bulk link variaveis a uma thing. Body: { variableIds: [...] } com a
// LISTA COMPLETA do estado final desejado. Backend calcula diff em
// transaction (cria ranges novas, remove as que sairam). Idempotente.
// Response: { added, removed, totalLinked }.
export async function linkThingVariables(thingId, variableIds) {
  const normalizedVariableIds = (variableIds || []).map((id) => Number(id));

  return request(`/api/v1/thing/${thingId}/variables`, 'PUT', {
    variableIds: normalizedVariableIds,
  });
}

// Lista leve de TODAS as things — popula multi-select "Em quais
// maquinas?" no cadastro de variavel. Filtros opcionais.
// Response: { data: [{ id, thing, name, applianceId, applianceName,
//                       customerId, customerName, type }] }.
export async function getThingsLight({ customerId, applianceId, type } = {}) {
  const params = new URLSearchParams();
  if (customerId) params.append('customerId', customerId);
  if (applianceId) params.append('applianceId', applianceId);
  if (type) params.append('type', type);
  const qs = params.toString();
  return request(`/api/v1/thing/list-light${qs ? `?${qs}` : ''}`);
}

// Criar variavel. thingId pode ser numero unico ou array (cria pra
// varios things de uma vez). Dispara variableSync no backend.
export async function createVariable(body) {
  return request(`/api/v1/variable`, 'POST', body);
}

// Atualizar variavel. Body opcional em todos os campos.
export async function updateVariable(id, body) {
  return request(`/api/v1/variable/${id}`, 'PATCH', body);
}

// Excluir variavel. Backend faz snapshot antes + finalize depois.
// Retorna { id }.
export async function deleteVariable(id) {
  return request(`/api/v1/variable/${id}`, 'DELETE');
}

export async function postEditRange(body) {
  return request(`api/v1/variable/ranges`, 'POST', body);
}

export async function getRangeGraph(id, variables) {
  return request(
    `/api/v1/variable/ranges?variables=[${variables}]&thingId=${id}`
  );
}

export async function getTotalEnergyGenerated(id) {
  return request(`/api/v1/appliance/total-energy-generated?applianceId=${id}`);
}

// Serie diaria de energia gerada (UCHA) ou consumida (EBA) dos ultimos
// N dias. Resposta: { result: { data: [{ date: 'YYYY-MM-DD', energy: kWh }] } }.
// energy positivo = geracao, negativo = consumo. Endpoint criado no
// backend (commit be6da11) — completa o gap do total-energy-generated
// que so retorna agregados mes/semana/dia.
export async function getApplianceEnergyDaily(applianceId, days = 7) {
  return request(`/api/v1/appliance/${applianceId}/energy-daily?days=${days}`);
}

// Serie diaria de vazao agregada da appliance (soma das things) dos
// ultimos N dias — usada no FlowHistoryChart pra EBAs (bombas). Resposta
// segue o mesmo envelope: { data: [{ date, flow }] }. Unidade do `flow`
// depende do backend (litros agregados por dia ou m³). O componente
// detecta a magnitude pra escolher unidade legivel (l/m³/Ml).
export async function getApplianceFlowDaily(applianceId, days = 7) {
  return request(`/api/v1/appliance/${applianceId}/flow-daily?days=${days}`);
}

// Disponibilidade real + MTBF/MTTR da aplicacao no periodo (das transicoes
// de status em thingstatushistoric). period: 'day'|'week'|'month'. Retorna
// { data: { availabilityPct, mtbfSeconds, mttrSeconds, failureCount,
//   uptimeSeconds, downtimeSeconds, totalSeconds, thingCount, ... } }.
export async function getApplianceAvailability(applianceId, period = 'day') {
  return request(
    `/api/v1/appliance/${applianceId}/availability?period=${period}`
  );
}

// Analitica de alarmes por maquina: downtime total + Pareto por tempo
// parado + MTBF/MTTR. period: 'day'|'week'|'month'. Retorna
// { data: { totalDowntimeSeconds, mtbfSeconds, mttrSeconds, failureCount,
//   pareto: [{ cause, count, downtimeSeconds }] } }.
export async function getThingAlarmAnalytics(thingId, period = 'week') {
  return request(`/api/v1/thing/${thingId}/alarm-analytics?period=${period}`);
}

// Things (maquinas) de uma usina, incluindo potenciaNominal (placa, kW) —
// usado pro fator de capacidade. Retorna a appliance com thing[].
export async function getApplianceThings(applianceId) {
  return request(`/api/v1/appliance/${applianceId}/things`);
}

// Tarifa efetiva da usina (R$/kWh): override da usina ?? default do cliente.
// Retorna { data: { effectiveTariff, applianceTariff, customerTariff } }.
export async function getApplianceTariff(applianceId) {
  return request(`/api/v1/appliance/${applianceId}/tariff`);
}

// Escrita (restrito a role privilegiado no backend). tariff/potenciaNominal
// null remove o valor.
export async function setApplianceTariff(applianceId, tariff) {
  return request(`/api/v1/appliance/${applianceId}/tariff`, 'PATCH', {
    tariff,
  });
}
export async function setCustomerTariff(customerId, tariff) {
  return request(`/api/v1/customer/${customerId}/tariff`, 'PATCH', { tariff });
}
export async function setThingNominal(thingId, potenciaNominal) {
  return request(`/api/v1/thing/${thingId}/nominal`, 'PATCH', {
    potenciaNominal,
  });
}

export async function getAllVariables(id, offset = 1, limit = 50, typeId) {
  return request(
    `/api/v1/variable/all?thingId=${id}&offset=${offset}&limit=${limit}&typeId=${typeId}`
  );
}

export async function getThingOperationByType(
  id,
  type,
  offset = 1,
  limit = 50
) {
  return request(
    `/api/v1/thing/${id}/operational/type/${type}?offset=${offset}&limit=${limit}`
  );
}

export async function getThingBlocks(id, offset = 1, limit = 50) {
  return request(
    `/api/v1/thing/${id}/type/block?offset=${offset}&limit=${limit}`
  );
}

export async function getThingTrips(id, offset = 1, limit = 50) {
  return request(
    `/api/v1/thing/${id}/type/trip?offset=${offset}&limit=${limit}`
  );
}

// Historico de atividade (transicoes de status) server-side, escopado por
// tenant. Usado pela aba "Historico" do painel de alertas — duravel e
// cross-operador (vs o buffer localStorage do client). Retorna
// { items: [{ thingName, thingDisplayName, parkName, fromStatus, toStatus, ts }], total }.
export async function getThingActivity({
  limit = 50,
  offset = 0,
  from,
  to,
} = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return request(`/api/v1/thing/activity?${params.toString()}`);
}

export function getThingEnergy(id) {
  return request(`/api/v1/thing/${id}/summary/energy`);
}

export async function getVariableGroups(thingId) {
  return request(`/api/v1/variable/menu?thingId=${thingId}`);
}

export async function getSetPointGroups(thingId) {
  return request(`/api/v1/setpoint/groups?thingId=${thingId}`);
}

export async function getAllSetPoints(thingId, groupId) {
  return request(`/api/v1/setpoint/all?thingId=${thingId}&groupId=${groupId}`);
}
export async function getCommandsSetPoints(thingId) {
  return request(
    `/api/v1/setpoint/all?thingId=${thingId}&groupId=2&setpoints=SP_Oper_Controle,SP_Oper_Ref_Ve,SP_Oper_Ref_Va,SP_Oper_Ref_Vb`
  );
}

export async function getThingSetPoints(thingId) {
  return request(`/api/v1/setpoint/all`, 'GET', null, null, { thingId });
}

export async function putSetPointsValue(body) {
  return request(`/api/v1/setpoint/thing`, 'PUT', body);
}

export async function getSetPointsHistoric(thingId, setpointId) {
  return request(
    `api/v1/setpoint/historic?thingId=${thingId}&setpointId=${setpointId}`
  );
}

export async function getCommandConfiguration(thingId, commandGroup) {
  return request(
    `api/v1/command/configuration?thingId=${thingId}&commandGroup=${commandGroup}`
  );
}

export async function getThingCommands(thingId) {
  return request(`api/v1/command/${thingId}`);
}

export async function getCommandHistoric(thingId, offSet = 0) {
  return request(`api/v1/command/historic/${thingId}/?offset=${offSet}`);
}

// Comando pendente no dispositivo (delta do shadow desired vs reported).
// Reflete comandos via shadow (rearme/habilitacao) que o device ainda nao
// confirmou — util em equipamento com conexao ruim. On-demand (1 call AWS
// por thing). Retorna { hasPending, pending: [{ command, desiredValue,
// reportedValue, since }] }.
export async function getPendingCommand(thingId) {
  return request(`api/v1/thing/${thingId}/pending-command`);
}

// Deteccao de anomalias v1 (estatistica): compara o valor atual de cada
// variavel da maquina com o baseline robusto (mediana/MAD, 14d, so amostras
// rodando). Retorna { data: { running, anomalies: [{variable, name, unit,
// current, typicalMedian, typicalP10, typicalP90, zScore, direction,
// severity}], checkedCount, baselineDays } }.
export async function getThingAnomalies(thingId) {
  return request(`/api/v1/thing/${thingId}/anomalies`);
}

// Parques com ALGUM comando pendente (shadow desired aguardando) em alguma
// maquina. Endpoint leve (cache 30s no back) — polling proprio no mapa.
// Retorna { data: { parkIds: number[] } }.
export async function getParksPendingCommands() {
  return request(`api/v1/park/pending-commands`);
}

// Resumo de turno gerado por IA: agrega a janela recente (transicoes,
// alarmes, paradas, geracao) e devolve briefing em PT-BR via LLM no backend
// (cache ~10min; summary null quando o LLM esta indisponivel — stats sempre
// vem). Retorna { data: { summary, llmEnabled, generatedAt, windowHours,
// stats } }.
export async function getShiftSummary(hours = 12) {
  return request(`api/v1/report/shift-summary?hours=${hours}`);
}

// Feature-flag leve: o backend tem ANTHROPIC_API_KEY configurada? Gateia a
// exibicao do botao "Resumo do turno" (sem chave, o botao nem aparece).
// Retorna { data: { llmEnabled: boolean } }.
export async function getLlmStatus() {
  return request(`api/v1/report/llm-status`);
}

// Copiloto do CCO: pergunta livre em PT-BR respondida pelo LLM via tool-use
// no backend (consulta SO dados do escopo do usuario; somente leitura).
// history = ultimos turnos [{role:'user'|'assistant', content}] pra manter
// contexto da conversa. Retorna { data: { answer, llmEnabled } }.
export async function askAssistant(question, history = []) {
  return request(`api/v1/report/assistant`, 'POST', { question, history });
}

// Ranking das maquinas (UCHA) que mais GERAM agora (modulo da potencia).
// Escopado por tenant no backend, cache 15s. Retorna
// { data: [{ thingId, thingName, generationKw, applianceName, parkName,
//   customerName }] }. Usado no dock "Top Geracao" do telao.
export async function getTopGeneration(limit = 10, mode = 'generation') {
  return request(`api/v1/thing/top-generation?limit=${limit}&mode=${mode}`);
}

export async function putThingCommand(body) {
  return request(`api/v1/command/send`, 'PUT', body);
}

export async function putThingCommandV2(body) {
  return request(`api/v1/command/v2/send`, 'PUT', body);
}

export async function putToggleCommand(body) {
  return request(`api/v1/command/enabling`, 'PUT', body);
}

export async function getEventList() {
  return request(`api/v1/alarmoperational/all`);
}

export async function getEstacoes(
  offset,
  limit,
  search,
  customerId,
  zoneId,
  companyId
) {
  return request(
    `/api/v1/park/?offset=${offset}&limit=${limit}&zone_id=${
      zoneId || ''
    }&customer_id=${customerId || ''}&company_id=${companyId}&search=${search}`
  );
}

export async function postEditEstacoes(Id, body) {
  return request(`/api/v1/park/update/${Id}`, 'PUT', body);
}

export async function postNewEstacoes(body) {
  return request(`/api/v1/park/`, 'POST', body);
}

export async function getListaDeEmpresasDoFiltroDaTelaRelacaoUsuarios() {
  return request(`/api/v1/customer/list-companies-and-customers`);
}

export async function getCustomers(
  offset,
  limit,
  search,
  customerId,
  statusCustomer
) {
  return request(
    `/api/v1/customer/?offset=${offset}&limit=${limit}&search=${search}&customerId=${customerId}${
      statusCustomer !== '' && statusCustomer !== undefined
        ? `&status=${statusCustomer}`
        : ''
    }`
  );
}

export async function getCustomersCnpj(companyId) {
  return request(`/api/v1/customer/data/${companyId}`);
}

export async function getUsers(
  offset,
  limit,
  search,
  roleCustomer,
  statusCustomer,
  id,
  isCustomer
) {
  const params = [
    `offset=${offset}`,
    `limit=${limit}`,
    search ? `search=${search}` : false,
    id ? (isCustomer ? `customer=${id}` : `company=${id}`) : false,
    roleCustomer !== '' && roleCustomer !== undefined
      ? `role=${roleCustomer}`
      : false,
    statusCustomer !== '' && statusCustomer !== undefined
      ? `status=${statusCustomer}`
      : false,
  ]
    .filter(Boolean)
    .join('&');

  return request(`/api/v1/user/?${params}`);
}

export async function getChangeHistory(
  offset,
  limit,
  { type, parkId, customerId, thingId } = {}
) {
  const params = [
    `offset=${offset}`,
    `limit=${limit}`,
    type ? `type=${type}` : false,
    parkId ? `parkId=${parkId}` : false,
    customerId ? `customerId=${customerId}` : false,
    thingId ? `thingId=${thingId}` : false,
  ]
    .filter(Boolean)
    .join('&');

  return request(`/api/v1/audit-logs/change-history?${params}`);
}

export async function getMaquina(
  offset,
  limit,
  search,
  parkId,
  customerId,
  includeInactive = false
) {
  return request(
    `/api/v1/appliance/filters?&offset=${offset}&limit=${limit}&search=${search}${
      customerId !== '' && customerId !== undefined
        ? `&customerId=${customerId}`
        : ''
    }${parkId !== '' && parkId !== undefined ? `&parkId=${parkId}` : ''}${
      includeInactive ? `&includeInactive=true` : ''
    }`
  );
}

export async function getMaquinaFilters() {
  return request(`/api/v1/appliance/create/filters`);
}

export async function getBlueboxSuggestedVariables() {
  return request(`/api/v1/appliance/create/bluebox/suggested-variables`);
}

export async function getMaquinaAppliances(query) {
  return request(`/api/v1/appliance/search`, 'GET', null, null, { query });
}

export async function postNewMaquina(body) {
  return request(`/api/v1/appliance/create`, 'POST', body);
}

export async function postEditMaquina(id, body) {
  return request(`/api/v1/appliance/update/${id}`, 'PUT', body);
}

// Ativar/desativar maquina (soft hide). active=false oculta a thing em
// todas as listagens do sistema (variaveis vinculadas seguem junto se
// nao houver outra thing ativa). Dados sao preservados no banco.
// Endpoint guard no backend: somente role higra/admin pode chamar.
// Implementacao backend em commit f485257 (higra-cco-backend).
export async function toggleMachineActive(thingId, active) {
  return request(`/api/v1/thing/${thingId}/active`, 'PATCH', { active });
}

export async function postEditStatusUser(userId, status) {
  return request(`/api/v1/user/status/${userId}`, 'PUT', { status });
}

export async function deleteExistentUser(userId) {
  return request(`/api/v1/user/delete/${userId}`, 'DELETE');
}

export async function getStatusUser(userId) {
  return request(`/api/v1/user/userStatus?Username=${userId}`);
}

export async function getCompanys() {
  return request(`api/v1/company`);
}

export async function postEditStatus(customerId, status) {
  return request(`/api/v1/customer/status/${customerId}`, 'PUT', { status });
}

export async function postNewClient(body) {
  return request(`/api/v1/customer`, 'POST', body);
}

export async function postNewUser(customer, company, body) {
  return request(`/api/v1/user?${customer}${company}`, 'POST', body);
}

export async function postEditUser(Id, body) {
  return request(`/api/v1/user/update/${Id}`, 'PUT', body);
}

// Admin define manualmente a senha de um usuario (sem depender do fluxo
// de "esqueci a senha"). Restrito a admin/higra no backend (Cognito
// AdminSetUserPassword, permanente).
export async function setUserPassword(Id, password) {
  return request(`/api/v1/user/${Id}/password`, 'PUT', { password });
}

export async function postEditClient(Id, body) {
  return request(`/api/v1/customer/update/${Id}`, 'PUT', body);
}

export async function postReadNotification(body) {
  return request(`api/v1/event/read`, 'POST', body);
}

export async function postArchiveNotification(body) {
  return request(`api/v1/event/archive`, 'POST', body);
}

export async function putSponsorNotification(body) {
  return request(`api/v1/event/sponsor`, 'PUT', body);
}

export async function getUserNotifications(
  offset,
  limit,
  type,
  search,
  status
) {
  return request('api/v1/event/all', false, false, false, {
    offset,
    limit,
    type,
    search,
    status,
  });
}

export async function getErrorAlertNotifications(limit, offset) {
  return request('api/v1/event/error-alert', 'GET', null, null, {
    limit,
    offset,
  });
}

export async function getUnreadErrorAlertNotificationsAmount(pageSize = 100) {
  const firstResponse = await getErrorAlertNotifications(pageSize, 1);
  const firstPageItems = Array.isArray(firstResponse?.data?.items)
    ? firstResponse.data.items
    : Array.isArray(firstResponse?.data)
    ? firstResponse.data
    : [];
  const totalPages = Number(firstResponse?.data?.totalPages ?? 1);

  let unreadAmount = firstPageItems.filter(
    ({ visualized }) => !visualized
  ).length;

  if (totalPages <= 1) return unreadAmount;

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getErrorAlertNotifications(pageSize, index + 2)
    )
  );

  remainingPages.forEach((response) => {
    const items = Array.isArray(response?.data?.items)
      ? response.data.items
      : Array.isArray(response?.data)
      ? response.data
      : [];

    unreadAmount += items.filter(({ visualized }) => !visualized).length;
  });

  return unreadAmount;
}

export async function patchErrorAlertVisualized(body) {
  return request('api/v1/event/error-alert/visualized', 'PATCH', body);
}

export async function getUserDeletedNotifications() {
  return request(`/api/v1/event/removedevent`);
}
export async function deleteUserNotification(id) {
  return request(`/api/v1/event/${id}`, 'DELETE');
}

export async function getNotificationsStatus() {
  return request(`api/v1/event/status`);
}

export async function putRearmeEvento(thingId) {
  return request(`api/v1/alarmoperational/rearm`, 'PUT', thingId);
}

export async function putStatusNotificacao(eventId, statusId) {
  return request(`api/v1/event/status`, 'PUT', { eventId, statusId });
}

export async function getNotificationAmount(
  offset,
  limit,
  type,
  search,
  status
) {
  return request(`api/v1/event/count`, false, false, false, {
    offset,
    limit,
    type,
    search,
    status,
  });
}

export async function putAddImagePin(body) {
  return request('api/v1/thing/point', 'POST', body);
}

export async function getImagePins(thingId) {
  return request(`api/v1/thing/${thingId}/point`);
}

export async function deleteImagePin(pinId) {
  return request(`api/v1/thing/point/${pinId}`, 'DELETE');
}

export async function patchImagePin(pinId, body) {
  return request(`api/v1/thing/point/${pinId}`, 'PATCH', body);
}

export async function getReport(
  initialDate,
  finalDate,
  thingsId,
  variablesId,
  applianceId,
  userUTC,
  group,
  setpoints
) {
  return request(
    `${BASE_URL}/api/v1/appliance/report/${applianceId}?initialDate=${initialDate}&finalDate=${finalDate}&thingsId=${thingsId}${
      variablesId ? `&variablesId=${variablesId}` : ''
    }&userUTC=${userUTC}${group ? '&group=true' : ''}${
      setpoints.length ? `&setpointsId=${setpoints}` : ''
    }`,
    'report',
    [],
    {}
  );
}

export async function getNewModelOfReport(
  thing,
  initialDate,
  finalDate,
  variables,
  setpoints
) {
  return request(
    `${BASE_URL}/api/v1/appliance/report/v2/${thing}`,
    'new-report',
    {
      initialDate,
      finalDate,
      variables,
      setpoints,
    },
    {
      timeout: 600000,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    }
  );
}

export async function getNewModelOfReportV3(
  thing,
  initialDate,
  finalDate,
  variables,
  setpoints
) {
  return request(
    `${BASE_URL}/api/v1/appliance/report/v3/${thing}/false`,
    'new-report',
    {
      initialDate,
      finalDate,
      variables,
      setpoints,
    },
    {
      // timeout: 600000,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    }
  );
}

export async function postAddRange(thingId, variableId, level, category, unit) {
  return request(`api/v1/variable/ranges`, 'POST', {
    thingId,
    variableId,
    level,
    category,
    unit,
    min: '0',
    max: '1',
  });
}

export async function getRendimentoHidraulico(thingId) {
  return request(`api/v1/thing/efficiency/${thingId}`);
}

export async function deleteRange(rangeId, thingId, variableId) {
  return request(`api/v1/variable/ranges`, 'DELETE', {
    id: rangeId,
    thingId,
    variableId,
  });
}

export async function getThingOperationalStatus(thingId) {
  return request(`api/v1/thing/operational-status/${thingId}`);
}

export async function getSetPoints() {
  return request(
    `api/v1/setpoint/filter?names=SP_Oper_Controle,SP_Oper_Ref_Vb,SP_Oper_Ref_Ve,SP_Oper_Ref_Va`
  );
}

export async function getVariables() {
  return request(
    `api/v1/variable/filter?names=CMD_Liga,CMD_Desliga,CMD_Reset_Horimetro,CMD_ModoAutomatico,CMD_ModoManual,CMD_Rearme`
  );
}

// server-env.eba-mp5sbykh.us-east-1.elasticbeanstalk.com/api/v1/appliance/report/5?initialDate=2021-03-10T03:00:00.000Z&finalDate=2021-03-25T17:51:01.131Z&thingsId=[5]&variablesId=[3,5,1]&userUTC=3
